import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import TrendingStrip from "../components/TrendingStrip";
import StatsHero from "../components/StatsHero";
import Sidebar from "../components/Sidebar";
import MarketCard from "../components/MarketCard";
import TopMoversStrip from "../components/TopMoversStrip";
import {
  fetchMarkets,
  fetchTrending,
  fetchStats,
  fetchCategories,
} from "../lib/api";
import { CircleNotch } from "@phosphor-icons/react";

export default function Dashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialCat = new URLSearchParams(location.search).get("cat");

  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(initialCat || null);
  const [order, setOrder] = useState("volumeNum");
  const [ascending, setAscending] = useState(false);

  // Sync category → URL so links from the hamburger menu update the bar AND
  // refreshing the page preserves the filter.
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const current = p.get("cat") || null;
    if (current === category) return;
    if (category) p.set("cat", category);
    else p.delete("cat");
    const qs = p.toString();
    navigate(`/${qs ? `?${qs}` : ""}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  // Also reflect URL → state when the user taps a different category link
  // from the mobile drawer (which navigates inside the same /).
  useEffect(() => {
    const nextCat = new URLSearchParams(location.search).get("cat") || null;
    if (nextCat !== category) setCategory(nextCat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const [markets, setMarkets] = useState([]);
  const [trending, setTrending] = useState([]);
  const [stats, setStats] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load static-ish bits once
  useEffect(() => {
    Promise.all([fetchTrending(20), fetchStats(), fetchCategories()])
      .then(([t, s, c]) => {
        setTrending(t.items || []);
        setStats(s);
        setCategories(c.items || []);
      })
      .catch(() => {});
  }, []);

  // Live market list
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchMarkets({
      limit: 60,
      order,
      ascending,
      tag_slug: category || undefined,
      search: search.trim() || undefined,
    })
      .then((d) => {
        if (!cancelled) setMarkets(d.items || []);
      })
      .catch(() => {
        if (!cancelled) setMarkets([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [category, order, ascending, search]);

  // Refresh stats every 60s
  useEffect(() => {
    const i = setInterval(() => {
      fetchStats().then(setStats).catch(() => {});
    }, 60000);
    return () => clearInterval(i);
  }, []);

  const enriched = useMemo(() => markets, [markets]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="dashboard-page">
      <Header search={search} setSearch={setSearch} />
      <TrendingStrip items={trending} />
      <StatsHero stats={stats} />
      <TopMoversStrip />

      <div className="mx-auto max-w-[1600px] flex">
        <Sidebar
          categories={categories}
          activeCategory={category}
          setCategory={setCategory}
          order={order}
          setOrder={setOrder}
          ascending={ascending}
          setAscending={setAscending}
        />

        <main className="flex-1 min-w-0 px-6 py-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.32em] font-mono text-edge">
                ◢ Markets Terminal
              </div>
              <h2 className="font-display text-2xl font-medium mt-1">
                {category
                  ? `${categories.find((c) => c.slug === category)?.label || category}`
                  : "All Markets"}
                <span className="ml-3 text-sm font-mono text-zinc-500 tabular-nums">
                  · {enriched.length} results
                </span>
              </h2>
            </div>
            <div className="hidden md:flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest">
              <span className="h-2 w-2 bg-profit animate-pulse-soft" />
              <span className="text-zinc-400">Live · auto-refresh 60s</span>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24 text-zinc-500 font-mono text-xs uppercase tracking-widest">
              <CircleNotch size={18} className="animate-spin mr-2" />
              Loading markets…
            </div>
          ) : enriched.length === 0 ? (
            <div
              data-testid="empty-state"
              className="text-center py-24 text-zinc-500 font-body"
            >
              No markets match your filters.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
              {enriched.map((m) => (
                <MarketCard key={m.id} market={m} />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
